import React, { useState, useMemo } from "react";

import {
  AHInput,
  AHButton,
  AHSectionHeader,
  AHSectionHeaderTitleLegacy,
  AHText,
  useAHListItemStyle,
  Box,
  useLayoutContext,
  ListItem,
  AHSeparator,
  SelectDot,
} from "arkh";

import { useKeyboard } from "../../OS/useKeyboard";

import { useSetCurrentStoreId } from "../../OS/Login/State";
import {
  ActionCard,
  ActionCardButton,
  ActionCardDescription,
  ActionCardIcon,
  ActionCardTitle,
} from "../../OS/Login/ActionCardTitle";
import { FormattedMessage, useIntl } from "react-intl";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { captureException } from "../../common/sentry";
import { graphql, useMutation } from "react-relay";
import {
  CreateStoreComponentMutation,
  CreateStoreComponentMutation$data,
  StoreTemplate,
} from "./__generated__/CreateStoreComponentMutation.graphql";
import { useAlert } from "../../common/Alert";

export const CreateStoreComponent = (props: { onCreatedStore: () => void }) => {
  const [storeName, setStoreName] = useState("");
  const availableRect = useKeyboard().availableRect;
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const listItemStyle = useAHListItemStyle();
  const [createStoreMutation, loading] =
    useMutation<CreateStoreComponentMutation>(graphql`
      mutation CreateStoreComponentMutation(
        $name: String!
        $template: StoreTemplate
      ) {
        createStore(storeName: $name, template: $template) {
          id
          name
          timezone
        }
      }
    `);
  const [template, setTemplate] = useState<StoreTemplate | null>(null);
  const [createdStore, setCreatedStore] =
    useState<CreateStoreComponentMutation$data | null>(null);

  const setCurrentStoreId = useSetCurrentStoreId();
  const Alert = useAlert();
  const createStore = () => {
    createStoreMutation({
      variables: { name: storeName, template },
      onCompleted: (data) => {
        setCreatedStore(data);
      },
      onError: (e) => {
        captureException(e);
        Alert.alert("Failed to create store", e.message);
      },
    });
  };
  const isValid = useMemo(() => storeName.length > 1, [storeName]);

  const intl = useIntl();

  if (createdStore) {
    return (
      <Box
        style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        space="medium"
        color="background"
      >
        <ActionCard>
          <ActionCardTitle>
            <FormattedMessage
              defaultMessage="Your store is ready!"
              description="CreateStoreComponent has created store title"
            />
          </ActionCardTitle>
          <AHText style={{ textAlign: "center" }} type="title-small">
            {createdStore.createStore?.name ?? ""}
          </AHText>
          <ActionCardIcon name="store" />
          <ActionCardDescription>
            <ActionCardDescription>
              <FormattedMessage
                defaultMessage="An empty store has been created for you. Start filling it with your routines, tasks and to-dos!"
                description="CreateStoreComponent has created store description"
              />
            </ActionCardDescription>
          </ActionCardDescription>
          <ActionCardButton
            onPress={() => {
              if (createdStore?.createStore?.id) {
                setCurrentStoreId(
                  createdStore.createStore.id,
                  createdStore.createStore.timezone
                );

                props.onCreatedStore();
              }
            }}
            title={intl.formatMessage({
              defaultMessage: "OK",
              description:
                "CreateStoreComponent has created store confirmation button label",
            })}
          />
        </ActionCard>
      </Box>
    );
  }

  return (
    <>
      <Box style={{ flex: 1 }}>
        <Box spaceHorizontal="medium" style={{ flex: 1 }}>
          <AHSectionHeader>
            <AHSectionHeaderTitleLegacy>
              <FormattedMessage
                defaultMessage="Store Name"
                description="CreateStoreComponent store name input label"
              />
            </AHSectionHeaderTitleLegacy>
          </AHSectionHeader>
          <AHInput
            editable
            autoFocus
            value={storeName}
            onChangeText={setStoreName}
            placeholder={intl.formatMessage({
              defaultMessage: "Store Name",
              description: "CreateStoreComponent store name input placeholder",
            })}
            returnKeyType="next"
            style={[
              listItemStyle.container,
              listItemStyle.bottomItem,
              listItemStyle.topItem,
            ]}
          />
          <AHSectionHeader>
            <AHSectionHeaderTitleLegacy>Template</AHSectionHeaderTitleLegacy>
          </AHSectionHeader>
          <TemplateItem
            isFirst
            title={
              <FormattedMessage
                defaultMessage="Empty"
                description="Create Store Template Empty Label"
              />
            }
            onPress={() => {
              setTemplate(null);
            }}
            selected={template === null}
          />
          <AHSeparator />
          <TemplateItem
            isLast
            title={intl.formatMessage({
              defaultMessage: "Grocery Store",
              description: "Create Store Template Grocery Label",
            })}
            onPress={() => {
              setTemplate("GROCERY_STORE");
            }}
            selected={template === "GROCERY_STORE"}
          />
        </Box>
      </Box>
      <Box
        style={{
          position: "absolute",
          left: layout.space.medium,
          right: layout.space.medium,
          bottom:
            Math.max(insets.bottom, availableRect.bottom) + layout.space.medium,
          height: 40,
        }}
      >
        <AHButton
          title={intl.formatMessage({
            defaultMessage: "Create Store",
            description: "CreateStoreComponent create store button title",
          })}
          disabled={!isValid}
          loading={loading}
          loadingTitle={intl.formatMessage({
            defaultMessage: "Creating Store",
            description:
              "CreateStoreComponent create store button loading title",
          })}
          hideIfDisabled
          themeType="primary"
          onPress={() => {
            createStore();
          }}
        />
      </Box>
    </>
  );
};

function TemplateItem(props: {
  isFirst?: boolean;
  isLast?: boolean;
  selected: boolean;
  onPress?: () => void;
  title: React.ReactNode;
}) {
  return (
    <ListItem
      onPress={props.onPress}
      isFirst={props.isFirst}
      isLast={props.isLast}
      left={<SelectDot selected={props.selected} selectedDotColor="primary" />}
    >
      <ListItem.Title>{props.title}</ListItem.Title>
    </ListItem>
  );
}
